import { useCallback, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Associations, InvoiceTemplate, ProfileItemResponse, Sending } from 'src/modules/invoice/models/invoiceTemplate';
import { FormProvider, useForm } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getInvoiceTemplateById, updateInvoiceTemplate, updateInvoiceTemplateSending } from 'src/shared/services/invoiceService';
import * as yup from 'yup';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, Typography } from '@mui/material';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { yupResolver } from '@hookform/resolvers/yup';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { CustomChip } from 'src/shared/components/organisms/customChip';
import { CreateTabAria, TabPanel } from 'src/shared/components/navigations/tabPanel';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { InvoiceTemplateInformation } from './invoiceTemplateInformation';
import { getSendingConfig, isSendingNotApproved, RenderInvoiceTemplateSending } from './renderInvoiceTemplateSending';

interface invTempPartialValues extends Pick<InvoiceTemplate, 'selection_id'> {
  name: string;
  email?: string;
  status: string;
  showFormInfo?: boolean;
  useLocalAssLogo?: boolean;
  useLocalAssAddress?: boolean;
  preReceiverText?: string;
  formTemplate?: string;
  expireDateFrom?: string | Dayjs | null;
  expireDateTom?: string | Dayjs | null;
  memberType?: number;
  newspaper?: number | null;
  recruit?: number;
  printZeroInvoice?: boolean;
  householdInvoice?: boolean;
  suppressAcknowledgement?: boolean;
  approvePaymentsGreaterThanInvoiced?: boolean;
  payIntervalFrom?: number;
  payIntervalTom?: number;
  resdate?: string | Dayjs | null;
  payedDateFrom?: string | Dayjs | null;
  payedDateTom?: string | Dayjs | null;
  associations?: Associations[];
  profilesIncludeItems?: ProfileItemResponse[];
  profilesExcludeItems?: ProfileItemResponse[];
  profilesMarkItems?: ProfileItemResponse[];
  sendings: Sending[];
}

type AdminInvoiceTemplateDetailProps = {
  openTemplateDetails: boolean;
  setOpenTemplateDetails: (onClose: boolean) => void;
  templateId: number;
  setTemplateId: (setTemplateId: number) => void;
  updateData: () => void;
};

export const AdminInvoiceTemplateDetail = (props: AdminInvoiceTemplateDetailProps) => {
  const { openTemplateDetails, setOpenTemplateDetails, templateId, setTemplateId, updateData } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const canEditTemplateInformation = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const [loading, setLoading] = useState(false);
  const [invoiceTemplate, setInvoiceTemplate] = useState<InvoiceTemplate>();
  const [activeTab, setActiveTab] = useState(0);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const invoiceTemplateSchema = yup.object().shape({
    name: yup.string().trim().required(t('common:validationMessages.required')),
    formTemplate: yup.string().optional(),
    sendings: yup.array(
      yup.object().shape({
        defaultHeading: yup.string().optional(),
        defaultText: yup.string().optional(),
        expireDate: yup.string().optional(),
        emailHeading: yup.string().optional(),
        emailText: yup.string().optional(),
      }),
    ),
  });

  const formMethods = useForm<invTempPartialValues>({
    resolver: yupResolver(invoiceTemplateSchema),
    mode: 'all',
    defaultValues: {
      name: '',
      status: '0',
      sendings: [],
    },
  });

  const { handleSubmit, reset, formState } = formMethods;

  // formState är en proxy som börjar bevaka ett fält först när det läses under rendering. Läses
  // isDirty bara inne i en callback uppdateras den aldrig, och varningen vid stängning uteblir.
  const { isDirty } = formState;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getInvoiceTemplateById(templateId);
      if (isMounted()) {
        setInvoiceTemplate(result.data);
        // Status "1" = dold, "0" = synlig. Andra värden tolkas som synlig ("0").
        reset({ ...result.data, status: result.data.status === '1' ? '1' : '0' });
      }
    } catch (err) {
      if (isMounted()) ConsoleHelper.log(err);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, reset, templateId]);

  useEffect(() => {
    if (templateId) {
      fetchData();
    }
  }, [fetchData, templateId]);

  const closeNow = () => {
    setActiveTab(0);
    setTemplateId(0);
    setOpenTemplateDetails(false);
    setConfirmCloseOpen(false);
  };

  // Att stänga med krysset eller Avbryt kastar det man skrivit. Är formuläret orört stängs det direkt,
  // annars frågar vi först.
  const onHandleClose = () => {
    if (isDirty) {
      setConfirmCloseOpen(true);
      return;
    }

    closeNow();
  };

  // Datumväljaren lägger ett Dayjs-objekt i formuläret när ett datum väljs; API:t tar YYYYMMDD (som utskicks-wizarden).
  const toApiDate = (value?: string | Dayjs | null) => {
    if (!value) return '';
    return dayjs.isDayjs(value) ? value.format('YYYYMMDD') : value;
  };

  // Samma format som utskicks-wizarden sparar: par av "grupp,post".
  const toProfilePairs = (items?: ProfileItemResponse[]) =>
    (items ?? []).flatMap((item) => [item.memberProfile_id, item.memberProfileItem_id]).join(',');

  const onSubmit = async (data: invTempPartialValues): Promise<boolean> => {
    setLoading(true);
    const dataToPost: InvoiceTemplate = {
      ...invoiceTemplate,
      template_id: invoiceTemplate?.template_id || 0,
      owner_id: invoiceTemplate?.owner_id || 0,
      approvePaymentsGreaterThanInvoiced: !!data.approvePaymentsGreaterThanInvoiced,
      payIntervalFrom: data.payIntervalFrom ?? 0,
      payIntervalTom: data.payIntervalTom ?? 0,
      name: data.name.trim(),
      email: data.email?.trim() ?? '',
      status: data.status,
      showFormInfo: !!data.showFormInfo,
      useLocalAssLogo: !!data.useLocalAssLogo,
      useLocalAssAddress: !!data.useLocalAssAddress,
      preReceiverText: data.preReceiverText ?? '',
      formTemplate: data.formTemplate,
      expireDateFrom: toApiDate(data.expireDateFrom),
      expireDateTom: toApiDate(data.expireDateTom),
      memberType: data.memberType,
      newspaper: data.newspaper ?? null,
      recruit: data.recruit,
      printZeroInvoice: !!data.printZeroInvoice,
      householdInvoice: !!data.householdInvoice,
      suppressAcknowledgement: !!data.suppressAcknowledgement,
      resdate: toApiDate(data.resdate),
      payedDateFrom: toApiDate(data.payedDateFrom),
      payedDateTom: toApiDate(data.payedDateTom),
      // Förenings-id:n kommaseparerade; tom sträng = samtliga föreningar.
      selection: (data.associations ?? []).map((association) => association.id).join(','),
      associations: data.associations,
      selection_id: data.selection_id ?? 0,
      profilesInclude: toProfilePairs(data.profilesIncludeItems),
      profilesIncludeItems: data.profilesIncludeItems,
      profilesExclude: toProfilePairs(data.profilesExcludeItems),
      profilesExcludeItems: data.profilesExcludeItems,
      profilesMark: toProfilePairs(data.profilesMarkItems),
      profilesMarkItems: data.profilesMarkItems,
      // Försändelserna sparas från sina egna flikar (PUT InvoiceTemplateSending). undefined utelämnas ur JSON-bodyn.
      sendings: undefined,
    };

    try {
      await updateInvoiceTemplate(dataToPost);
      if (isMounted()) {
        updateData();
        // Modalen stängs inte vid sparning. Mallen har flera flikar och man vill ofta spara en
        // flik och fortsätta med nästa. Det sparade läggs i state så flikarna visar rätt värden
        // utan att formuläret laddas om, vilket annars skulle kasta osparade ändringar på andra flikar.
        // Forsändelserna skickas inte med i mallsparningen (sendings är undefined ovan), så de måste
        // behållas här. Annars försvinner flikarna för huvudutskick och påminnelser efter en sparning.
        setInvoiceTemplate((prev) => ({ ...dataToPost, sendings: prev?.sendings }));
        // Det sparade blir formulärets nya utgångsläge, annars ser det ändrat ut när man stänger.
        reset(data);
        toast.success(<>{t('invoice:adminInvoiceTemplatePage.invTempUpdated')}</>);
      }

      return true;
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log('Error occurred:', e);
        toast.error(<>{t('invoice:adminInvoiceTemplatePage.invTempUpdatedError')}</>);
      }

      return false;
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const onHandleSave = () => {
    handleSubmit(onSubmit)();
  };

  // Antalet räknas på mallen som den är sparad, så Räkna antal sparar först och räknar sedan.
  // Returnerar om sparningen gick igenom, så att räkningen kan avbrytas om den inte gjorde det.
  const saveBeforeCount = async () => {
    let saved = false;

    await handleSubmit(async (data) => {
      saved = await onSubmit(data);
    })();

    return saved;
  };

  // Spara på en försändelseflik: uppdaterar bara den försändelsen via PUT InvoiceTemplateSending.
  const onSubmitSending = async (data: invTempPartialValues, sendingIndex: number) => {
    const original = invoiceTemplate?.sendings?.[sendingIndex];
    if (!original) return;
    setLoading(true);
    // Att spara försändelsen godkänner den (samma som InvoiceTemplateService.Update gör för sendings).
    const sendingToPost: Sending = { ...original, ...data.sendings[sendingIndex], notReady: false, textApproved: true };

    try {
      await updateInvoiceTemplateSending(sendingToPost);

      // Blanketten väljs under huvudutskicket men hör till hela mallen, så försändelsens
      // sparning når den inte. Den skickas med en egen uppdatering, och bara när den ändrats,
      // så att en vanlig textsparning inte rör mallens övriga fält.
      if (invoiceTemplate && data.formTemplate !== invoiceTemplate.formTemplate) {
        await updateInvoiceTemplate({ ...invoiceTemplate, formTemplate: data.formTemplate });
      }

      if (isMounted()) {
        updateData();
        // Som ovan: modalen står kvar. Den sparade försändelsen läggs tillbaka i state så att
        // godkännandemärkningen på fliken och blankettjämförelsen utgår från det som nu är sparat.
        setInvoiceTemplate((prev) =>
          prev
            ? {
                ...prev,
                formTemplate: data.formTemplate,
                sendings: (prev.sendings ?? []).map((s, i) => (i === sendingIndex ? sendingToPost : s)),
              }
            : prev,
        );
        toast.success(<>{t('invoice:adminInvoiceTemplatePage.invTempUpdated')}</>);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log('Error occurred:', e);
        toast.error(<>{t('invoice:adminInvoiceTemplatePage.invTempUpdatedError')}</>);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const onHandleSaveSending = (sendingIndex: number) => {
    handleSubmit((data) => onSubmitSending(data, sendingIndex))();
  };

  // Behåll originalindex i sendings-listan eftersom formfälten adresseras med det.
  const visibleSendings = (invoiceTemplate?.sendings ?? [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.sendingType !== InvoiceSendingTypeIntegerEnum.Email && item.customerEditEnabled);

  const tabsId = `invoice-template-${templateId}`;

  // Flik 0 är Mallinformation, resten är försändelser i samma ordning som flikarna.
  const activeSending = activeTab > 0 ? visibleSendings[activeTab - 1] : undefined;

  // Mallinformationen går att spara även utan behörigheten för de interna inställningarna. De fälten
  // är då dolda och skickas tillbaka oförändrade, medan urvalet är kundens att ändra.
  const activeTabSave = (() => {
    if (activeSending) return () => onHandleSaveSending(activeSending.index);
    return onHandleSave;
  })();

  // Knapparna ligger i en fast fot, som i utskicksbeställningen, så Spara är nåbart utan att
  // scrolla förbi editorn. Mallinformation sparar hela mallen (PUT InvoiceTemplate),
  // försändelseflikarna sparar sin försändelse (PUT InvoiceTemplateSending).
  const renderTabActions = (onSave?: () => void) => (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        px: 3,
        py: 2,
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Button variant='outlined' color='primary' disableElevation onClick={onHandleClose}>
        {t('common:button.cancel')}
      </Button>
      {onSave && (
        <Button variant='contained' color='primary' disableElevation disabled={loading} onClick={onSave}>
          {t('common:button.save')}
        </Button>
      )}
    </Box>
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Bred dialog: texterna redigeras med förhandsvisning bredvid, vilket inte ryms i standardbredden. */}
        <GenericDialog
          disableBackdropClosing
          open={openTemplateDetails}
          padding={0}
          maxWidth='xl'
          disableEnforceFocus
          dialogTitle={invoiceTemplate?.name}
          onClose={onHandleClose}
          onSave={onHandleSave}
          showDialogActions={false}
          dialogContent={
            <Box>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Tabs value={activeTab} onChange={(_e, newValue) => setActiveTab(newValue)} variant='scrollable' allowScrollButtonsMobile>
                  <Tab label={t('invoice:adminInvoiceTemplatePage.templateInformation.templateInformation')} {...CreateTabAria(false, tabsId, 0)} />
                  {visibleSendings.map(({ item }, tabIndex) => (
                    <Tab
                      key={item.templateSendings_id}
                      label={
                        <Stack direction='row' spacing={1} alignItems='center' sx={{ '& .MuiChip-filled ': { borderRadius: 1 } }}>
                          <span>{t(getSendingConfig(item.sendingType).titleKey)}</span>
                          {isSendingNotApproved(item) && (
                            <CustomChip label={t('invoice:adminInvoiceTemplatePage.notApproved')} color='error' size='small' />
                          )}
                        </Stack>
                      }
                      {...CreateTabAria(false, tabsId, tabIndex + 1)}
                    />
                  ))}
                </Tabs>
              </Box>
              {/* Panelerna hålls monterade så att redigeringsläget inte nollställs vid flikbyte. */}
              <TabPanel value={activeTab} index={0} padding {...CreateTabAria(true, tabsId, 0)}>
                {invoiceTemplate && <InvoiceTemplateInformation readOnly={!canEditTemplateInformation} saveBeforeCount={saveBeforeCount} />}
              </TabPanel>
              {visibleSendings.map(({ item, index }, tabIndex) => (
                <TabPanel
                  key={item.templateSendings_id}
                  value={activeTab}
                  index={tabIndex + 1}
                  padding
                  {...CreateTabAria(true, tabsId, tabIndex + 1)}
                >
                  <RenderInvoiceTemplateSending loading={loading} sendingItem={item} index={index} setLoading={setLoading} />
                </TabPanel>
              ))}

              {renderTabActions(activeTabSave)}
            </Box>
          }
        />

        <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)} maxWidth='xs' fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{t('invoice:adminInvoiceTemplatePage.unsaved.title')}</DialogTitle>
          <DialogContent>
            <Typography variant='body2' color='text.secondary'>
              {t('invoice:adminInvoiceTemplatePage.unsaved.text')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button variant='text' onClick={() => setConfirmCloseOpen(false)}>
              {t('invoice:adminInvoiceTemplatePage.unsaved.keepEditing')}
            </Button>
            <Button variant='contained' color='error' disableElevation onClick={closeNow}>
              {t('invoice:adminInvoiceTemplatePage.unsaved.discard')}
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};
